import { GridColDef, GridRenderCellParams, GridValueFormatterParams } from '@mui/x-data-grid';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import useProductOptions from '../../../basics/options/product.options';
import sortFilterOptions from '../../../basics/options/sotrFilter.options';
import useTherapeuticAreaOptions from '../../../basics/options/therapeuticArea.options';
import { SortFilter } from '../../../generated/maya.types';
import { VeevaSyncStatusBadgeMap } from 'basics/constants/common.constant';
import { CiStatus, VeevaSyncStatus } from 'basics/enums/maya.enums';
import GridListAction from 'components/GridListAction/GridListAction';
import { ProductsContext } from 'contexts/products';
import Tag from 'yoda-ui/components/Tags/Tag';
import { YodaBorderRadius, YodaColors, YodaFontSize, YodaFontWeight, YodaSpacing } from 'yoda-ui/yodaTheme';

const useCustomerInitiativeListConfig = (
  editCiCallback: (ciId: string) => void,
  duplicateCiCallback: (ciId: string) => void,
  addChildAction: (ciId: string) => void,
  openModalCallback: (ciId: string) => void,
  syncCiCallback: (ciId: string) => void,
  approveCiCallback: (ciId: string) => void,
  therapeuticArea: string[],
) => {
  const { t } = useTranslation();
  const flags = useFlags();
  const { allProducts } = useContext(ProductsContext);
  const currentTA = therapeuticArea ?? '';

  const CiStatusBadgeMap = {
    [CiStatus.DRAFT as string]: {
      label: t('maya_status_draft'),
      bgColor: YodaColors.errorPurple,
      color: YodaColors.errorPurpleLight,
    },
    [CiStatus.ACTIVE as string]: {
      label: t('maya_status_active'),
      bgColor: YodaColors.greenSuccessLight,
      color: YodaColors.greenSuccessDark,
    },
  };

  const ciTitleFieldConfig = {
    name: 'name',
    placeholder: t('maya_ci_form_ci_title_placeholder'),
    defaultValue: '',
    required: false,
  };

  const ciTherapeuticAreaFieldConfig = {
    name: 'therapeuticArea',
    options: useTherapeuticAreaOptions(allProducts),
    placeholder: t('maya_ci_form_ci_therapeutic_area_placeholder'),
    defaultValue: [],
    required: false,
    disableRenderTags: true,
  };

  const ciProductFieldConfig = {
    name: 'product',
    options: useProductOptions(currentTA, allProducts, ''),
    placeholder: t('maya_ci_form_ci_product_placeholder'),
    defaultValue: [],
    required: false,
    disableRenderTags: true,
  };

  const ciSortFieldConfig = {
    name: 'sort',
    options: sortFilterOptions,
    defaultValue: SortFilter.NEWEST,
    required: false,
    disableRenderTags: true,
  };

  const customerInitiativeListColumnConfig: GridColDef[] = [
    {
      field: 'title',
      headerName: t('maya_customer_initiative_list_initiative_name_header_label'),
      flex: 0.5,
      sortable: false,
      cellClassName: 'gridlist-cell-bold',
    },
    {
      field: 'product',
      headerName: t('maya_customer_initiative_list_product_header_label'),
      valueGetter: (params: GridValueFormatterParams) => {
        return allProducts.find((product) => product.productExternalID === params.value)?.productName || '';
      },
      flex: 0.2,
      sortable: false,
    },
    {
      field: 'veevaSyncStatus',
      headerName: t('maya_veeva_sync_status_header_label'),
      flex: 0.15,
      renderCell: (cellValues: GridRenderCellParams) => {
        const badge = VeevaSyncStatusBadgeMap[cellValues.row.veevaSyncStatus || VeevaSyncStatus.PENDING];
        return badge && (
          <Tag
            fitContent
            bgcolor={ badge.bgColor }
            color={ badge.color }
            fontWeight={ YodaFontWeight.medium }
            fontSize={ YodaFontSize.small }
            paddingY={ YodaSpacing.xxxxSmall }
            paddingX={ YodaSpacing.xSmall }
            width={ 'auto' }
            height={ '20px' }
            borderRadius={ YodaBorderRadius.xSmall }
          >
            { badge.label }
          </Tag>
        );
      },
      sortable: false,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.1,
      renderCell: (cellValues: GridRenderCellParams) => {
        const badge = CiStatusBadgeMap[cellValues.row.status];
        return badge && (
          <Tag
            fitContent
            bgcolor={ badge.bgColor }
            color={ badge.color }
            fontWeight={ YodaFontWeight.medium }
            fontSize={ YodaFontSize.small }
            paddingY={ YodaSpacing.xxxxSmall }
            paddingX={ YodaSpacing.xSmall }
            width={ 'auto' }
            height={ '20px' }
            borderRadius={ YodaBorderRadius.xSmall }
          >
            { badge.label }
          </Tag>
        );
      },
      sortable: false,
    },
    {
      field: 'action',
      headerName: '',
      disableColumnMenu: true,
      hideSortIcons: true,
      renderCell: (params: GridRenderCellParams) => {
        const actions = [
          {
            label: t('actions_edit_label'),
            callback: () => { editCiCallback(params.row._id); },
          },
          {
            label: t('actions_duplicate_label'),
            callback: () => { duplicateCiCallback(params.row._id); },
          },
          {
            label: t('maya_customer_initiative_list_add_child_cep'),
            callback: () => {
              addChildAction(params.row._id);
            },
          },
        ];

        if (flags.mayaBiogenlinc3810EnableCiDeleting && params?.row?.status !== CiStatus.ACTIVE) {
          actions.splice(2, 0, {
            label: t('maya_action_delete_ci_label'),
            callback: () => {
              openModalCallback(params.row._id);
            },
          });
        }

        if (flags.mayaBiogenlinc2489VeevaSyncButtons && params.row.status !== CiStatus.ACTIVE) {
          actions.push({
            label: t('maya_action_sync_ci_label'),
            callback: () => {
              syncCiCallback(params.row._id);
            },
          });
        }

        if (flags.mayaBiogenlinc5411DisplayActivateCiCta && params.row.status !== CiStatus.ACTIVE) {
          actions.push({
            label: t('maya_action_activate_ci_label'),
            callback: () => {
              approveCiCallback(params.row._id);
            },
          });
        }

        return (
          <GridListAction
            actions={ actions }
          />
        );
      },
      align: 'right',
      headerAlign: 'right',
      flex: 0.01,
      sortable: false,
    },
  ];

  return {
    customerInitiativeListColumnConfig,
    ciTitleFieldConfig,
    ciTherapeuticAreaFieldConfig,
    ciProductFieldConfig,
    ciSortFieldConfig,
  };
};

export default useCustomerInitiativeListConfig;
